import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, GraduationCap, Building2, Users, Globe2, Trophy, 
  Lightbulb, Target, TrendingUp, Award, BookOpen, Mic2, MapPin
} from 'lucide-react';

const VernandMorencyPage = () => {
  const accomplishments = [
    { icon: Trophy, title: 'Former NFL & MLB Athlete', description: 'Professional athlete who competed at the highest levels in both football and baseball' },
    { icon: GraduationCap, title: 'Harvard Fellow', description: 'Doctoral candidate and Fellow at Weatherhead Center for International Affairs (WCIA) at Harvard University, bridging academia and real-world impact' },
    { icon: Lightbulb, title: 'Housing Policy Innovator', description: 'Recognized expert in innovative real estate policy and affordable housing solutions' },
    { icon: Target, title: 'Jock Tax Pioneer', description: 'Developer of groundbreaking frameworks leveraging athlete taxation for social impact' }
  ];

  const keyInitiatives = [
    {
      title: 'Quantum Affordable Housing Initiative',
      description: 'A transformative approach to housing policy that redirects transient capital from athlete taxation into permanent affordable housing infrastructure.',
      stats: [
        { value: '1.6B', label: 'People lacking adequate housing globally' },
        { value: '96K', label: 'New homes needed daily by 2030' },
        { value: '50%', label: 'Of jock tax proposed for Housing Trusts' }
      ]
    }
  ];

  const globalModels = [
    { country: 'Austria', model: 'The Funding Model', description: "A 1% housing tax on income funds Vienna's renowned 'Gemeindebauten' social housing system" },
    { country: 'Japan', model: 'The Zoning Model', description: 'National zoning laws streamline development, keeping Tokyo affordable despite massive density' },
    { country: 'Spain', model: 'The Incentive Model', description: "The 'Beckham Law' (2005) demonstrated targeted tax regimes can attract talent while generating revenue" }
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 mb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
                Keynote Speaker & Researcher
              </span>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Vernand <span className="text-gradient-quantum">Morency</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-6">
                Doctoral Candidate & Fellow at Weatherhead Center for International Affairs (WCIA) at Harvard University
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Former NFL and MLB athlete turned housing policy innovator. Vernand bridges the worlds of 
                sports, community development, and policy reform, offering unique perspectives on leveraging 
                influence for social impact.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">Harvard Fellow</span>
                <span className="px-4 py-2 rounded-full bg-secondary/10 text-secondary text-sm font-medium">Former NFL Player</span>
                <span className="px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium">Policy Innovator</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1 glow-primary"
                >
                  Register for Conference
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/research"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
                >
                  <BookOpen className="w-5 h-5" />
                  Read the Research
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden glass-card">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ea17ed55-887b-400a-8525-513b1791bc1e/artifacts/ox1jdeul_WhatsApp%20Image%202026-02-03%20at%2006.19.07%20%281%29.jpeg"
                  alt="Vernand Morency"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <GraduationCap className="w-4 h-4" />
                    <span>Fellow at WCIA, Harvard</span>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Accomplishments */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-playfair text-3xl font-bold text-center mb-12">
            Career & Accomplishments
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {accomplishments.map((item, index) => (
              <div key={index} className="glass-card rounded-xl p-6 text-center hover:-translate-y-1 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Vision */}
      <section className="px-6 md:px-12 lg:px-24 mb-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-8">
            "How Athletes and Their Jock Taxes Can Solve the Affordable Housing Crisis"
          </h2>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            Vernand's groundbreaking research presents a unique approach to leveraging high-income taxation 
            for social impact development. By ring-fencing transient capital from the "Jock Tax" – taxes 
            athletes pay in every jurisdiction they play – he proposes directing 50% of this revenue into 
            permanent Housing Trusts that fund new construction and rent-controlled units.
          </p>
          <div className="glass-card rounded-2xl p-8 inline-block">
            <p className="text-xl font-medium italic text-primary">
              "Transforming communities by leveraging the financial energy generated by sports 
              into permanent infrastructure like affordable housing."
            </p>
          </div>
        </div>
      </section>

      {/* Key Initiative */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          {keyInitiatives.map((initiative, index) => (
            <div key={index} className="glass-card rounded-3xl p-8 md:p-12">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-playfair text-2xl font-bold mb-2">{initiative.title}</h3>
                  <p className="text-muted-foreground">{initiative.description}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {initiative.stats.map((stat, i) => (
                  <div key={i} className="text-center p-6 rounded-xl bg-background/50 border border-border">
                    <p className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Global Precedents */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-playfair text-3xl font-bold text-center mb-4">
            Global Housing Models
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Drawing inspiration from proven international approaches to affordable housing policy
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {globalModels.map((model, index) => (
              <div key={index} className="glass-card rounded-xl p-6 hover:-translate-y-1 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <Globe2 className="w-6 h-6 text-primary" />
                  <span className="font-semibold text-lg">{model.country}</span>
                </div>
                <h4 className="font-semibold text-primary mb-2">{model.model}</h4>
                <p className="text-sm text-muted-foreground">{model.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Partners */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-playfair text-3xl font-bold text-center mb-12">
            Strategic Partners
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card rounded-xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary">MIT</span>
              </div>
              <h4 className="font-semibold mb-2">MIT Media Lab, City Science</h4>
              <p className="text-sm text-muted-foreground">Strategic Research Partner using AI to simulate economic impacts</p>
            </div>
            <div className="glass-card rounded-xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-8 h-8 text-secondary" />
              </div>
              <h4 className="font-semibold mb-2">NexusImpact.AI</h4>
              <p className="text-sm text-muted-foreground">Quantum-enabled social impact investment platform</p>
            </div>
            <div className="glass-card rounded-xl p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-accent" />
              </div>
              <h4 className="font-semibold mb-2">Setpoint.io</h4>
              <p className="text-sm text-muted-foreground">Ensuring capital flows with speed and transparency</p>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking at Conference */}
      <section className="px-6 md:px-12 lg:px-24 mb-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-full" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <Mic2 className="w-8 h-8 text-primary" />
                <span className="text-sm uppercase tracking-widest text-secondary font-medium">
                  Keynote Speaker
                </span>
              </div>
              <h2 className="font-playfair text-3xl font-bold mb-4">
                Hear Vernand Live at the Quantum Housing Conference
              </h2>
              <p className="text-muted-foreground mb-6">
                Join us at Harvard University on April 17th, 2026 for Vernand's opening keynote 
                on "The Vision for Global Affordable Housing Transformation."
              </p>
              <div className="flex items-center gap-6 mb-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>Tsai Auditorium, Harvard</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  <span>Opening Keynote</span>
                </div>
              </div>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
              >
                Register Now
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center glass-card rounded-3xl p-12">
          <h2 className="font-playfair text-3xl font-bold mb-4">
            Learn More About the Initiative
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Interested in partnering with the Quantum Affordable Housing Initiative? 
            Explore sponsorship opportunities and join us in transforming communities.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/sponsorship"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
            >
              Partnership Opportunities
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="https://www.quantumaffordablehousing.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
            >
              <Globe2 className="w-5 h-5" />
              Visit Website
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VernandMorencyPage;
